var XMLNode		 	= require('../xml/XMLNode'),
	BigNumber		= require('../binding/type/BigNumber'),
	CalendarDate 	= require('../binding/type/CalendarDate'),
	Numeric 		= require('../binding/type/Numeric'),
	BooleanType 	= require('../binding/type/BooleanType'),
	Currency 		= require('../binding/type/Currency');

/**
 * The Cell class represents a single cell within the spreadsheet
 * that has to be assigned to a particular row. A Cell stores
 * information supporting several types of data.
 *
 * @param {Type} oType - The data type of the cell
 * @param {SharedStrings} oSharedStrings - SharedStrings instance that will be used by all Cells
 * @class
 */
function Cell(oType, oSharedStrings) {
	XMLNode.call(this, Cell.metadata);

	Object.defineProperty(this, 'type', { value: Cell.DataType.SharedString, writable: true });

	this._shared = oSharedStrings;
	this._oFallbackType = null;
	this._applyType(oType);
}

Cell.prototype = Object.create(XMLNode.prototype);
Cell.prototype.constructor = Cell;

Cell.DataType = {
	Boolean: 'b',
	Date: 'd',
	InlineString: 'inlineStr', // We do not support inline strings
	Number: 'n',
	SharedString: 's',
	FormularString: 'str'
};

Cell._cellTypeProperty = 'cellType';

Cell.metadata = {
	tag: 'c',
	attributes: [
		{
			id: 'reference',
			name: 'r',
			value: ''
		},
		{
			id: 'style',
			name: 's',
			value: '0' // Default style id
		}
	],
	properties: [
		{
			id: 'text',
			tag: 'v'
		}
	]
};

/**
 * Applies the given type to the current Cell and maps to the
 * corresponding Office Open XML type definition.
 *
 * @param {Type} oType - Value of type {@link Type}
 * @private
 */
Cell.prototype._applyType = function(oType) {

	this.bindingType = oType;

	switch (oType.constructor) {
		case BooleanType:
			this.type = Cell.DataType.Boolean;

			if (!oType.isCustomFormatted()) {
				this.addAttribute({
					id: Cell._cellTypeProperty,
					name: 't',
					value: Cell.DataType.Boolean
				});
			}
			break;

		case CalendarDate:
			this.type = Cell.DataType.Date;
			break;

		case Numeric:
		case Currency:
			this.type = Cell.DataType.Number;

			/*
			 * As long as _applyType is only called once during
			 * initialization, the fallbackType can be registered
			 * without any additional check.
			 */
			this._oFallbackType = new BigNumber(oType);
			this._oFallbackType.registerAt(oType.styles);

			break;

		default: // Text is the default case
			this.addAttribute({
				id: Cell._cellTypeProperty,
				name: 't',
				value: Cell.DataType.SharedString // Default cell type
			});
			this.type = Cell.DataType.SharedString;
	}
};

/**
 * Applies the specified Style to the cell.
 *
 * @param {number|string} nStyle - Instance of Excel.objects.Style
 */
Cell.prototype.setStyle = function(nStyle) {
	this.style.value = nStyle;
};

/**
 * Applies the specified cell location as reference. The reference
 * must match the regular expression of Open Document SpreadsheetML
 * sheet format. Starting with at least one uppercase letter for the
 * column followed by a number without leading zeros for the row.
 * [A-Z]+[1-9]+[0-9]*
 *
 * @param {string} sReference - Reference of the cells' location
 */
Cell.prototype.setReference = function(sReference) {
	if (typeof sReference != 'string'
		|| !sReference.match(/^[A-Z]+[1-9]+[0-9]*$/g)) {
		throw new Error('The given Cell reference "' + sReference + '" does not match the reference pattern.');
	}

	this.reference.value = sReference;
};

/**
 * Assigns the value to the current Cell. The method requires a
 * property reference and a business object that gets bound depending
 * on the property. It is also possible to provide an array of
 * properties in case of aggregated columns.
 *
 * @param {(string|Array)} sProperty - single property or Array of properties
 * @param {Object} oBusinessObject - BusinessObject that gets bound to this Cell
 */
Cell.prototype.setValue = function(sProperty, oBusinessObject) {
	var value, bindingType;

	bindingType = this.bindingType;
	value = bindingType.getValue(sProperty, oBusinessObject);

	if (!this._isValuePresent(value)) {

		/*
		 * We need to clear the value due to the fact
		 * that we are re-using the Cell for each row
		 */
		this.text.value = '';

		return;
	}

	switch (this.type) {
		case Cell.DataType.Boolean:

			/* String representations other than "true" will result in the boolean value false */
			if (typeof value === 'string') {
				value = value.toLowerCase() === 'true';
			}
			this.text.value = value ? '1' : '0';

			break;
		case Cell.DataType.Date:
			this.text.value = value;

			break;
		case Cell.DataType.Number:
			if (bindingType.isIEEE754Compliant(value)) {
				this.text.value = value;

				if (this[Cell._cellTypeProperty]) {
					this.remove(Cell._cellTypeProperty);
				}

				break;
			}

			/* Handle fallback case and insert value as SharedString */
			bindingType = this._oFallbackType;
			value = bindingType.getValue(sProperty, oBusinessObject);

			if (!this[Cell._cellTypeProperty]) {
				this.addAttribute({
					id: Cell._cellTypeProperty,
					name: 't',
					value: Cell.DataType.SharedString // Default cell type
				});
			}


			// CAUTION: break is omitted intentionally

		default: // SharedString is the default case
			this.text.value = this._shared.insert(value);
	}

	/*
	 * Forces a style update once the value changed. This allows cell
	 * specific styles for currency types. Due to the fact that the
	 * Style ID gets cached within the Type, there is no performance
	 * impact even on large data sets.
	 */
	this.setStyle(bindingType.getStyleId(oBusinessObject, value));
};

/**
 * Checks whether there is a value provided or not. Allowed values
 * are Stringsas well as numeric and boolean values. Empty Strings
 * will be evaluated as no value, but the numeric value 0 is a value.
 *
 * @param {(string|number|boolean|Array)} data - The value that gets checked
 * @returns {boolean} - true if a value is present, false if not
 * @private
 */
Cell.prototype._isValuePresent = function(data) {
	return ((typeof data === 'string' && !!data)
		|| typeof data === 'number'
		|| typeof data === 'boolean'
		|| data instanceof Date);
};

module.exports = Cell;


//////////////////
// WEBPACK FOOTER
// ./core/xlsx/src/data/Cell.js
// module id = 3
// module chunks = 0